import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
// External components
import { Box, Card, CardContent, Typography } from "@mui/material";
// Component
import PersonSection from "../PersonSection";
import { Table } from "../DataTable/components";
import DEFAULT_COLUMNS from "./Enums/Columns";
import useElectronicBill from "../../hooks/useElectronicBill";
import { useElectronic } from "../../context/ElectronicContext";
import { ELECTRONIC_BILLS_DOCUMENT_TYPES } from "../../Enums/ElectronicBill";

function ElectronicBillDetail({ electronicDocument }) {
  const { useCoinDetail } = useElectronic();
  const { formatBillDetailToTable } = useElectronicBill();
  const { toExchangeRate, currentSymbolCoin } = useCoinDetail;

  const checkBillType = useCallback(
    billTypeEnumType =>
      Number(electronicDocument.ElectronicBillDocumentType.id) ===
      billTypeEnumType,
    [electronicDocument],
  );

  const isDebitNote = () =>
    checkBillType(ELECTRONIC_BILLS_DOCUMENT_TYPES.DEBIT_NOTE);
  const isCreditNote = () =>
    checkBillType(ELECTRONIC_BILLS_DOCUMENT_TYPES.CREDIT_NOTE);
  const isExportationElectronicBill = () =>
    checkBillType(ELECTRONIC_BILLS_DOCUMENT_TYPES.EXPORTATION_ELECTRONIC_BILL);

  const productsFormattedToTable = electronicDocument?.details?.map(
    (productFormatted, index) =>
      formatBillDetailToTable(
        {
          ...productFormatted,
          index: index + 1,
        },
        currentSymbolCoin,
      ),
  );
  const receptor =
    electronicDocument?.Bill?.User?.UserBillingProfile ||
    electronicDocument?.ReferencedDocument?.Bill?.User?.UserBillingProfile;
  const totalAmounts = useMemo(
    () => ({
      TotalExempt:
        (electronicDocument.TotalGoodsExempt || 0) +
        (electronicDocument.TotalServicesExempt || 0),
      TotalExonerated:
        (electronicDocument.TotalGoodsExonerated || 0) +
        (electronicDocument.TotalServicesExonerated || 0),
      TotalTaxed: electronicDocument.TotalTaxed,
      TotalNetSell:
        (electronicDocument.SubTotal_Bill || 0) -
        (electronicDocument.Discount || 0),
      TotalSell: electronicDocument.TotalBill,
      Discount: electronicDocument.Discount,
      TotalGoodsExempt: electronicDocument.TotalGoodsExempt,
      TotalGoodsExonerated: electronicDocument.TotalGoodsExonerated,
      TotalGoodsTaxed: electronicDocument.TotalGoodsTaxed,
      TotalServicesExempt: electronicDocument.TotalServicesExempt,
      TotalServicesExonerated: electronicDocument.TotalServicesExonerated,
      TotalServicesTaxed: electronicDocument.TotalServicesTaxed,
    }),
    [electronicDocument],
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1rem",
        flexDirection: "column",
        maxWidth: "100%",
        maxHeight: "100%",
      }}
    >
      <Card
        color="optionCardSelect"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <Typography fontWeight="bold" typography="modeColor" variant="h6">
          Resumen de factura electrónica
        </Typography>
        <CardContent
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: "1rem",
            justifyContent: "space-between",
          }}
        >
          <CardContent
            sx={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Clave:
              </Typography>
              <p>{`  ${electronicDocument.Key}`}</p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Consecutivo:
              </Typography>
              <p>{`  ${electronicDocument?.InternConsecutive || "--"}`}</p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Condición de venta:
              </Typography>
              <p>{`${
                electronicDocument?.SellConditionMH?.Name_SellCondition || "--"
              }`}</p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Tipo de documento:
              </Typography>
              <p>
                {electronicDocument.ElectronicBillDocumentType?.Description ||
                  ""}
              </p>
            </div>
            <div className="content-flex-row-start">
              <p>
                <Typography fontWeight="bold" typography="modeColor">
                  Moneda:
                </Typography>
                {electronicDocument?.Coin?.Name_Coin}
              </p>
            </div>
          </CardContent>
          <CardContent
            sx={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Plazo crédito: ---{" "}
              </Typography>
              <p>
                Fecha{!electronicDocument.EmitedDay && " (Tentativa)"}:
                {electronicDocument.EmitedDay
                  ? new Date(electronicDocument.EmitedDay).toLocaleDateString()
                  : new Date().toLocaleDateString().concat(" Verificando...")}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Medio de pago:
              </Typography>
              <p>
                {` ${electronicDocument.PaymentMethodMH?.Name_PaymentMethod}`}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Tipo de cambio:
              </Typography>
              <p>
                {electronicDocument?.Coin?.Buy_Coin
                  ? (1 / electronicDocument.Coin.Buy_Coin).toFixed(2)
                  : "--"}
              </p>
            </div>
          </CardContent>
        </CardContent>
      </Card>
      {(isCreditNote() || isDebitNote()) &&
        electronicDocument?.ReferenceReason && (
          <Card
            color="optionCardSelect"
            sx={{
              paddingY: "1rem",
              paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold" typography="modeColor" variant="h6">
              Detalle de nota
            </Typography>

            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Código:
              </Typography>
              <p>{electronicDocument?.ReferenceCode}</p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Razón:
              </Typography>
              <p>{electronicDocument?.ReferenceReason}</p>
            </div>
          </Card>
        )}
      <div className="content-grid-column">
        {electronicDocument?.BillingProfile && (
          <Card
            color="optionCardSelect"
            sx={{
              paddingY: "1rem",
              paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <Typography fontWeight="bold" typography="modeColor" variant="h6">
              Datos emisor
            </Typography>
            <PersonSection
              person={{
                ...electronicDocument?.BillingProfile,
                activityEmitter: electronicDocument.Bill.fk_activity_emitter,
              }}
            />
          </Card>
        )}

        {receptor && (
          <Card
            color="optionCardSelect"
            sx={{
              paddingY: "1rem",
              paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <Typography fontWeight="bold" typography="modeColor" variant="h6">
              Datos Receptor
            </Typography>
            <PersonSection
              isExportationBill={isExportationElectronicBill()}
              person={{
                ...receptor,
                activityReceiver:
                  electronicDocument?.Bill?.fk_activity_receiver,
              }}
            />
          </Card>
        )}
      </div>

      <Card
        color="optionCardSelect"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography fontWeight="bold" typography="modeColor" variant="h6">
          Detalles
        </Typography>
        <Table columns={DEFAULT_COLUMNS} data={productsFormattedToTable} />
      </Card>

      <Card
        color="optionCardSelect"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography fontWeight="bold" typography="modeColor" variant="h6">
          Totales
        </Typography>

        <div className="content-grid-column">
          <CardContent
            sx={{
              padding: 0,
              gap: "1rem",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Descuento total:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.Discount || 0).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total exento:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalExempt || 0).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total exonerado:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalExonerated || 0).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total exento en productos:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalGoodsExempt || 0).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total exonerado en productos:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(
                    totalAmounts.TotalGoodsExonerated || 0,
                  ).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total de productos con impuesto:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalGoodsTaxed || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
          <CardContent
            sx={{
              padding: 0,
              gap: "1rem",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total de la venta:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalSell || 0).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total de servicios exentos:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalServicesExempt || 0).toFixed(
                    2,
                  )}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total de servicio exonerado:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(
                    totalAmounts.TotalServicesExonerated || 0,
                  ).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total impuestos de servicio:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalServicesTaxed || 0).toFixed(
                    2,
                  )}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total de impuestos:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalTaxed || 0).toFixed(2)}
              </p>
            </div>
            <div className="content-flex-row-start">
              <Typography fontWeight="bold" typography="modeColor">
                Total de venta neta:
              </Typography>
              <p>
                {currentSymbolCoin +
                  toExchangeRate(totalAmounts.TotalNetSell || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </div>
      </Card>
    </Box>
  );
}

ElectronicBillDetail.propTypes = {
  electronicDocument: PropTypes.object.isRequired,
};
export default ElectronicBillDetail;
